<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import boomerangBlackLogo from "@/assets/images/boomerang_black_logo.png";
import DashboardFooter from "@/components/dashboard/DashboardFooter.vue";
import { useZendeskChat } from "@/composables/useZendeskChat.ts";
import {
  resetPrimarySecondaryThemeColors,
  setPrimarySecondaryThemeColors,
} from "@/plugins/vuetify/config.ts";
import CollaboratorService from "@/services/collaboratorService.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { appConfig } from "@/types/appConfig.type.ts";

import type {
  Collaborator,
  KeyAccountNetwork,
  Role,
} from "@/types/services/auth.type.ts";

type NavigationItem = {
  value: string;
  label: string;
  icon: string;
  routeName: string;
  permissions: string[];
};

const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const {
  mount: mountZendesk,
  unmount: unmountZendesk,
  show: showZendesk,
} = useZendeskChat();
const showCollaborators = ref<boolean>(false);
const minWidth = ref<string>("0px");
const selectedCollaborator = ref<HTMLElement | null>(null);
const isSwitchingCollaborator = ref<boolean>(false);

const navigationItems = computed<NavigationItem[]>(() => [
  {
    value: "dashboard",
    label: t("common.navigation.dashboard"),
    icon: "mdi-home",
    routeName: "dashboard",
    permissions: ["KEY_ACCOUNT_ACCOUNT_READ_PERMISSION"],
  },
  {
    value: "repairFiles",
    label: t("common.navigation.repairFiles"),
    icon: "mdi-tag-outline",
    routeName: "repairFiles",
    permissions: ["KEY_ACCOUNT_VIEW_ALL_SUMMARY_REPAIR_FILE"],
  },
  {
    value: "keyAccountProducts",
    label: t("common.navigation.keyAccountProducts"),
    icon: "mdi-tools",
    routeName: "keyAccountProducts",
    permissions: ["KEY_ACCOUNT_PRODUCT_READ_PERMISSION"],
  },
  {
    value: "users",
    label: t("common.navigation.users"),
    icon: "mdi-account-multiple",
    routeName: "users",
    permissions: ["KEY_ACCOUNT_ACCOUNT_READ_PERMISSION"],
  },
  {
    value: "transporters",
    label: t("common.navigation.transporters"),
    icon: "mdi-truck-fast-outline",
    routeName: "expressistConnexions",
    permissions: ["KEY_ACCOUNT_TRANSPORTER_READ_PERMISSION"],
  },
  {
    value: "environmentalPerformanceReport",
    label: t("common.navigation.environmentalPerformanceReport"),
    icon: "mdi-leaf",
    routeName: "environmentalPerformanceReport",
    permissions: [],
  },
]);

const selectedItem = computed(() => route.meta.menuSelected ?? route.name);
const hideHeader = computed<boolean>(() => route.meta.hideHeader === true);
const visibleNavigationItems = computed(() =>
  navigationItems.value.filter(
    (item) =>
      item.permissions.length === 0 ||
      authStore.hasPermission(item.permissions),
  ),
);
const currentNetwork = computed<KeyAccountNetwork | null>(
  () => authStore.userDetail.network ?? null,
);
const appBarLogo = computed<string>(
  () => currentNetwork.value?.logo || boomerangBlackLogo,
);
const currentCollaborator = computed<Collaborator | null>(
  () => authStore.currentCollaborator,
);
const activeCollaborators = computed<Collaborator[]>(() =>
  (authStore.userDetail.collaborators ?? []).filter(
    (collaborator: Collaborator): boolean => collaborator.status === "active",
  ),
);
const accountRole = computed<string>(() => {
  const role: Role | undefined = authStore.userDetail.roles?.[0];
  return role?.title ?? "";
});
const hideCollaboratorMenu = computed<boolean>(
  () => activeCollaborators.value.length <= 1,
);
const profileActive = computed<boolean>(() =>
  route.fullPath.startsWith("/profile"),
);

const selectNavigationItem = async (item: NavigationItem): Promise<void> => {
  if (route.name !== item.routeName) {
    await router.push({ name: item.routeName });
  }
};

const logout = async (): Promise<void> => {
  await authStore.logoutAction();
};

const openHelp = (): void => {
  if (!appConfig.VITE_ZENDESK_URL) return;

  window.open(appConfig.VITE_ZENDESK_URL, "_blank", "noopener,noreferrer");
};

const selectProfile = async (): Promise<void> => {
  if (router.hasRoute("profile")) {
    await router.push({ name: "profile" });
  }
};

const selectCollaborator = async (
  collaborator: Collaborator,
): Promise<void> => {
  if (
    isSwitchingCollaborator.value ||
    isActiveCollaborator(collaborator.technical_id)
  )
    return;

  isSwitchingCollaborator.value = true;
  try {
    await CollaboratorService.switchCollaborator(collaborator.technical_id);
    await authStore.me();
    authStore.selectedCollaborator(collaborator);
    showCollaborators.value = false;
  } finally {
    isSwitchingCollaborator.value = false;
  }
};

const isActiveCollaborator = (collaboratorId: number): boolean =>
  currentCollaborator.value?.technical_id === collaboratorId;

watch(showCollaborators, async (isOpen: boolean): Promise<void> => {
  if (!isOpen) return;

  await nextTick();
  minWidth.value = `${(selectedCollaborator.value?.clientWidth ?? 0) + 1}px`;
});

watch(
  currentNetwork,
  (network: KeyAccountNetwork | null): void => {
    if (
      network?.custom_theme_enabled &&
      network.primary_color &&
      network.secondary_color
    ) {
      setPrimarySecondaryThemeColors(
        network.primary_color,
        network.secondary_color,
      );
      return;
    }

    resetPrimarySecondaryThemeColors();
  },
  { immediate: true },
);

onMounted((): void => {
  mountZendesk();
  showZendesk();
});

onUnmounted((): void => {
  resetPrimarySecondaryThemeColors();
  unmountZendesk();
});
</script>

<template>
  <v-app>
    <v-navigation-drawer rail permanent :border="false" color="secondary">
      <v-list density="compact" nav>
        <v-tooltip v-for="item in visibleNavigationItems" :key="item.value">
          {{ item.label }}
          <template #activator="{ props }">
            <v-list-item
              class="nav-element"
              v-bind="props"
              :value="item.value"
              :active="selectedItem === item.value"
              active-class="active-nav-element"
              @click="selectNavigationItem(item)"
            >
              <v-icon :icon="item.icon" />
            </v-list-item>
          </template>
        </v-tooltip>
      </v-list>

      <template #append>
        <v-list density="compact" nav>
          <v-tooltip>
            {{ t("common.navigation.help") }}
            <template #activator="{ props }">
              <v-list-item
                class="nav-element"
                v-bind="props"
                value="help"
                @click="openHelp"
              >
                <v-icon icon="mdi-account-question-outline" />
              </v-list-item>
            </template>
          </v-tooltip>

          <v-tooltip>
            {{ t("common.navigation.logout") }}
            <template #activator="{ props }">
              <v-list-item
                class="nav-element"
                v-bind="props"
                value="exit"
                @click="logout"
              >
                <v-icon icon="mdi-exit-to-app" />
              </v-list-item>
            </template>
          </v-tooltip>
        </v-list>
      </template>
    </v-navigation-drawer>

    <v-app-bar v-if="!hideHeader">
      <div class="logo-wrapper">
        <img alt="logo Boomerang" class="logo" :src="appBarLogo" />
      </div>

      <v-spacer />

      <v-tooltip location="bottom">
        {{ t("common.navigation.profile") }}
        <template #activator="{ props }">
          <v-btn-icon
            class="nav-btn"
            v-bind="props"
            icon="mdi-account-outline"
            :color="profileActive ? 'primary' : 'black'"
            variant="tonal"
            @click="selectProfile"
          />
        </template>
      </v-tooltip>

      <button
        v-if="currentCollaborator"
        ref="selectedCollaborator"
        class="selected-collaborator"
        type="button"
        :disabled="hideCollaboratorMenu"
        @click="showCollaborators = !showCollaborators"
      >
        <span class="d-flex flex-column align-start">
          <span class="selected-collaborator-name">
            {{
              currentCollaborator.firstname + " " + currentCollaborator.lastname
            }}
          </span>
          <span class="selected-collaborator-role">{{ accountRole }}</span>
        </span>
        <v-icon
          v-if="!hideCollaboratorMenu"
          :icon="showCollaborators ? 'mdi-chevron-down' : 'mdi-chevron-up'"
        />
      </button>

      <div
        v-if="showCollaborators"
        class="collaborators-list"
        :style="{ minWidth }"
      >
        <ul>
          <li
            v-for="collaborator of activeCollaborators"
            :key="collaborator.technical_id"
            :class="{
              active: isActiveCollaborator(collaborator.technical_id),
              disabled: isSwitchingCollaborator,
            }"
            @click="selectCollaborator(collaborator)"
          >
            {{ collaborator.firstname + " " + collaborator.lastname }}
            <v-icon
              v-if="isActiveCollaborator(collaborator.technical_id)"
              icon="mdi-check"
            />
          </li>
        </ul>
      </div>
    </v-app-bar>

    <v-main class="main">
      <slot />
    </v-main>
    <DashboardFooter v-if="route.name === 'dashboard'" />
  </v-app>
</template>

<style>
.v-navigation-drawer__content::-webkit-scrollbar-track {
  -webkit-box-shadow: inset 0 0 6px #5d5d5d;
}

.v-navigation-drawer__content::-webkit-scrollbar {
  width: 0;
}

.v-navigation-drawer__content::-webkit-scrollbar-thumb {
  -webkit-box-shadow: inset 0 0 6px #424242;
}

.logo-wrapper {
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 20px;
}

.logo {
  align-items: center;
  justify-content: center;
  max-width: 600px;
  max-height: 48px;
  object-fit: contain;
}

.main {
  background-color: var(--default-background-color);
}

.nav-element,
.active-nav-element,
.nav-element.v-list-item--active,
.nav-element .v-list-item__overlay,
.nav-element .v-ripple__container,
.active-nav-element .v-list-item__overlay,
.active-nav-element .v-ripple__container {
  border-radius: var(--default-radius);
}

.nav-element,
.active-nav-element,
.nav-element.v-list-item--active {
  overflow: hidden;
}

.nav-element.v-list-item--active,
.active-nav-element {
  background-color: rgb(var(--v-theme-surface));
}

.nav-element.v-list-item--active .v-list-item__overlay,
.active-nav-element .v-list-item__overlay {
  background-color: transparent;
  opacity: 0;
}

.active-nav-element .v-icon,
.active-nav-element .v-list-item__prepend > .v-icon,
.nav-element.v-list-item--active .v-icon,
.nav-element.v-list-item--active .v-list-item__prepend > .v-icon {
  color: rgb(var(--v-theme-secondary));
}

.selected-collaborator {
  height: 100%;
  display: flex;
  min-width: 250px;
  flex-direction: row;
  align-items: center;
  padding-left: 20px;
  padding-right: 20px;
  justify-content: space-between;
}

.selected-collaborator:hover {
  cursor: pointer;
}

.selected-collaborator:disabled {
  cursor: default;
}

.selected-collaborator-name {
  color: rgb(var(--v-theme-secondary));
  font-weight: bold;
}

.selected-collaborator-role {
  font-size: 13px;
  color: #616161;
}

.collaborators-list {
  right: 0;
  top: 64px;
  z-index: 1010;
  position: fixed;
  width: fit-content;
}

.collaborators-list ul li {
  list-style: none;
  padding: 10px 20px;
  color: rgb(var(--v-theme-secondary));
  font-weight: bold;
  font-size: 14px;
  display: flex;
  align-items: center;
  background-color: white;
  border-top: 1px solid var(--vuetify-grey);
  border-left: 1px solid var(--vuetify-grey);
  border-right: 1px solid var(--vuetify-grey);
  border-bottom: unset;
  justify-content: space-between;
}

.collaborators-list ul li:last-child {
  border-bottom: 1px solid var(--vuetify-grey);
}

.collaborators-list ul li:hover {
  background-color: var(--default-background-color);
  cursor: pointer;
}

.collaborators-list ul li.active {
  background-color: #f8f8f8;
  cursor: pointer;
}

.collaborators-list ul li.active:hover {
  background-color: var(--default-background-color);
}

.collaborators-list ul li.disabled {
  cursor: wait;
  opacity: 0.7;
}
</style>
